import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useConfirm } from 'material-ui-confirm';

import { canManagePaymentProviderSelector } from 'domain/env/envSelector';
import { currentCompanyNameSelector } from 'domain/companies';
import { hasNonDisconnectedPaymentProviderSelector } from 'domain/paySettings/selectorForPermissions';

import {
  changeStatusForProviderAction,
  connectProviderAction,
  disconnectProviderAction,
} from 'domain/paySettings/actions';

import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ContextMenuButton from 'components/mui/ContextMenuButton';

import { PaymentProviderStatuses } from 'domain/paySettings/helpers';
import type { PaymentProviderName } from 'domain/paySettings/helpers';
import { contextMenuActionsList } from './contextMenuActionsList';
import { useConnectPayload } from './useConnectPayload';

import { createStatuses, Button, Container } from './styled';

interface ProviderProps {
  title: string;
  status: string;
  icon: string;
  name: PaymentProviderName;
  id: string | null;
  setSelectedProviderId: (id: string | null) => void;
}

const Provider: React.FC<ProviderProps> = ({ title, status, icon, name, id, setSelectedProviderId }) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const confirm = useConfirm();

  const { canManagePaymentProvider, companyName, hasNonDisconnectedPaymentProvider } = useSelector((state: unknown) => ({
    canManagePaymentProvider: canManagePaymentProviderSelector(id)(state),
    companyName: currentCompanyNameSelector(state),
    hasNonDisconnectedPaymentProvider: hasNonDisconnectedPaymentProviderSelector(state),
  }));

  const params = useMemo(() => ({ providerName: name, apiKey: 'key', companyName }), [name, companyName]);
  const connectPayload = useConnectPayload(params);

  const onConnectClick = useCallback(() => {
    dispatch(connectProviderAction(connectPayload));
  }, [dispatch, connectPayload]);

  const onContinueKYCClick = useCallback(() => {
    dispatch(
      changeStatusForProviderAction({
        nextStatus: PaymentProviderStatuses.kycApproved,
        id,
      }),
    );
  }, [dispatch, id]);

  const onDisconnectClick = useCallback(() => {
    dispatch(disconnectProviderAction({ id, providerName: name }));
  }, [dispatch, id, name]);

  const onCancelSetupClick = useCallback(
    (group: { name?: string } = {}) => {
      confirm({
        title: formatMessage(
          {
            id: 'configurations.company.paymentProviders.cancelSetupModal.title',
            defaultMessage: 'Cancel payment provider setup?',
          },
          { groupName: group.name },
        ),
        description: formatMessage(
          {
            id: 'configurations.company.paymentProviders.cancelSetupModal.description',
            defaultMessage:
              'Are you sure you want to cancel the setup process? Any information you have entered will be lost.',
          },
          { groupName: group.name },
        ),
        confirmationText: formatMessage(
          {
            id: 'configurations.company.paymentProviders.cancelSetupModal.confirmBtn',
            defaultMessage: 'Cancel Setup',
          },
          { groupName: group.name },
        ),
        confirmationButtonProps: { color: 'error' },
        cancellationText: formatMessage({
          id: 'configurations.company.paymentProviders.cancelSetupModal.cancelBtn',
          defaultMessage: 'Go Back',
        }),
      })
        .then(onDisconnectClick)
        .catch(() => {});
    },
    [confirm, formatMessage, onDisconnectClick],
  );

  const onEditClick = useCallback(() => {
    setSelectedProviderId(id);
  }, [setSelectedProviderId, id]);

  const contextMenuActions = useMemo(
    () =>
      ({
        'configurations.company.paymentProviders.provider.contextMenu.viewTransactions': {
          onClick: () => {},
        },
        'configurations.company.paymentProviders.provider.contextMenu.disconnect': {
          onClick: onDisconnectClick,
          isHidden: status === PaymentProviderStatuses.disconnected,
        },
      }) as Record<string, { onClick: () => void; isHidden?: boolean }>,
    [status, onDisconnectClick],
  );

  const optionsList = useMemo(
    () =>
      contextMenuActionsList
        .map(({ id, defaultMessage, ...rest }) => ({
          id,
          title: { id, defaultMessage },
          ...rest,
          ...contextMenuActions[id],
        }))
        .filter(({ isHidden }) => !isHidden),
    [contextMenuActions],
  );

  const renderButton = useCallback(
    ({ status }: { status: string }) => {
      if (status === PaymentProviderStatuses.disconnected) {
        return (
          <Button onClick={onConnectClick} disabled={hasNonDisconnectedPaymentProvider}>
            {formatMessage({ id: 'settings.connections.table.connect', defaultMessage: 'Connect' })}
          </Button>
        );
      }

      if (!canManagePaymentProvider) return null;

      switch (status) {
        case PaymentProviderStatuses.connected:
          return (
            <Stack direction="row" spacing={1}>
              <ContextMenuButton options={optionsList} contextMenuName="PaymentProvider" />
              <Button variant="outlined" onClick={onEditClick}>
                {formatMessage({ id: 'button.edit', defaultMessage: 'Edit' })}
              </Button>
            </Stack>
          );
        case PaymentProviderStatuses.kycPending:
          return (
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" color="error" onClick={onCancelSetupClick as React.MouseEventHandler<HTMLButtonElement>}>
                {formatMessage({
                  id: 'configurations.company.paymentProviders.provider.button.cancelSetup',
                  defaultMessage: 'Cancel setup',
                })}
              </Button>
              <Button onClick={onContinueKYCClick}>
                {formatMessage({
                  id: 'configurations.company.paymentProviders.provider.button.continueKYC',
                  defaultMessage: 'Continue kyc',
                })}
              </Button>
            </Stack>
          );
        default: {
          return null;
        }
      }
    },
    [
      formatMessage,
      onConnectClick,
      onEditClick,
      optionsList,
      onContinueKYCClick,
      onCancelSetupClick,
      canManagePaymentProvider,
      hasNonDisconnectedPaymentProvider,
    ],
  );

  return (
    <Container>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flex="1" flexWrap="nowrap">
        <Stack display="flex" direction="row" spacing={1} alignItems="center">
          <Stack
            justifyContent="center"
            alignItems="center"
            sx={(theme) => ({
              padding: theme.spacing(0.5, 1),
              border: 1,
              borderColor: theme.palette.grey[200],
              backgroundColor: theme.palette.common.white,
              borderRadius: 2,
              minHeight: 34,
            })}
          >
            <img width={40} height="auto" src={icon} alt={title} />
          </Stack>
          <Typography>{[title]}</Typography>
          {createStatuses[status]}
        </Stack>
        <Box>{renderButton({ status })}</Box>
      </Stack>
    </Container>
  );
};

export default Provider;
